import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { F, FxFields, MixFields, ToolFields, UploadForm } from "../components/studio/Fields";
import { StorageMeter } from "../components/studio/StorageMeter";
import { ManageList } from "../components/studio/ManageList";
import { SocialForm } from "../components/studio/SocialForm";
import { AccountForm } from "../components/studio/AccountForm";
import { LiveToggle } from "../components/studio/LiveToggle";
import { StatsPanel } from "../components/studio/StatsPanel";
import { ForgotForm } from "../components/ForgotForm";

const AdminLogin = () => {
  const { setUser } = useAuth();
  const [forgot, setForgot] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.role !== "admin") setError("Esta cuenta no es la cuenta del DJ.");
      setUser(data);
    } catch (err) { setError(errMsg(err)); }
  };
  if (forgot) return <div className="max-w-sm"><ForgotForm onBack={() => setForgot(false)} testPrefix="admin-forgot" /></div>;
  return (
    <form onSubmit={submit} className="max-w-sm space-y-4" data-testid="admin-login-form">
      <F label="Correo" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" required />
      <F label="Contraseña" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" required />
      {error && <p className="text-sm text-[#FCA5A5]" data-testid="admin-login-error">{error}</p>}
      <button data-testid="admin-login-submit" className="w-full py-3.5 bg-[#1FD1FF] text-black font-mono text-sm uppercase tracking-widest">Entrar al estudio</button>
      <button type="button" data-testid="admin-forgot-link" onClick={() => setForgot(true)} className="font-mono text-xs text-[#1FD1FF] hover:text-white">Olvidé mi contraseña</button>
    </form>
  );
};

const TABS = [["manage", "Gestionar"], ["stats", "Estadísticas"], ["mix", "Subir mix"], ["tool", "Subir herramienta"], ["fx", "Subir efecto"], ["social", "Redes sociales"], ["account", "Mi cuenta"]];

export default function Studio() {
  const { user } = useAuth();
  const [data, setData] = useState({ mixes: [], tools: [], fx: [] });
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    Promise.all([api.get("/mixes"), api.get("/tools"), api.get("/fx")])
      .then(([m, t, f]) => setData({ mixes: m.data ?? [], tools: t.data ?? [], fx: f.data ?? [] }))
      .catch(() => {});
    setTick((x) => x + 1);
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  return (
    <main data-testid="admin-page" className="max-w-[1500px] mx-auto px-4 lg:px-10 pt-28 pb-20">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#1FD1FF]">Estudio privado</p>
      <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9] mb-10">Panel del DJ</h1>
      {user === null ? null : user?.role !== "admin" ? <AdminLogin /> : (
        <>
          <LiveToggle />
          <StorageMeter refreshKey={tick} />
          <Tabs defaultValue="manage">
            <TabsList className="rounded-none bg-[#0A1424] mb-10 flex-wrap h-auto justify-start">
              {TABS.map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-[#1FD1FF] data-[state=active]:text-black">{l}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="manage"><ManageList data={data} onChange={load} /></TabsContent>
            <TabsContent value="stats"><StatsPanel data={data} /></TabsContent>
            <TabsContent value="mix" className="max-w-3xl">
              <UploadForm endpoint="/admin/mixes" testid="admin-upload-mix-submit" onDone={load}><MixFields /></UploadForm>
            </TabsContent>
            <TabsContent value="tool" className="max-w-3xl">
              <UploadForm endpoint="/admin/tools" testid="admin-upload-tool-submit" onDone={load}><ToolFields /></UploadForm>
            </TabsContent>
            <TabsContent value="fx" className="max-w-3xl">
              <UploadForm endpoint="/admin/fx" testid="admin-upload-fx-submit" onDone={load}><FxFields /></UploadForm>
            </TabsContent>
            <TabsContent value="social" className="max-w-3xl"><SocialForm /></TabsContent>
            <TabsContent value="account"><AccountForm /></TabsContent>
          </Tabs>
        </>
      )}
    </main>
  );
}
